import Link from 'next/link';
import { memo, useCallback } from 'react';
import { useSelector } from 'react-redux';

import { DownloadIcon } from '@air/design-icons';
import { IconButton } from '@air/primitive-icon-button';
import { Tooltip } from '@air/primitive-tooltip';
import { VisuallyHidden } from '@air/primitive-visually-hidden';
import { useShortIdContext } from '@air/provider-short-id';

import { usePublicPermissions } from '~/components/PublicPermissionsProvider';
import { PublicBoardStats } from '~/components/v2/components/board/PublicBoardStats';
import { PublicSignUpButton } from '~/components/v2/components/board/PublicSignUpButton';
import { BoardBottombar } from '~/components/v2/components/board/shared/BoardBottombar';
import { AccountInformation } from '~/components/v2/components/shared/account/AccountInformation';
import { AirLogo } from '~/components/v2/components/shared/branding/AirLogo';
import { usePublicWhiteLabel } from '~/components/v2/hooks/useWhiteLabel';
import { Routes } from '~/constants/routes';
import { useIsSearchActive } from '~/hooks/search/useIsSearchActive';
import { useDownloadPublicBoard } from '~/hooks/useDownloadPublicBoard';
import { useHideAirLogo } from '~/hooks/useHideAirLogo';
import { useAccountContext } from '~/providers/AccountProvider';
import { centralizedBoardIdSelector } from '~/store/centralizedBoard/selectors';
import { canGenerateZip } from '~/utils/permissions/taskPermissions';

export const PublicBoardBottomBar = memo(() => {
  const { permissions } = usePublicPermissions();
  const boardId = useSelector(centralizedBoardIdSelector);
  const { downloadPublicBoard } = useDownloadPublicBoard();
  const { isSearchActive } = useIsSearchActive();
  const { presentationSettings } = useShortIdContext();
  const hideDownloadAll = presentationSettings?.hideDownloadAll === true;
  const isDownloadVisible = canGenerateZip(permissions) && !!boardId && !hideDownloadAll;
  const { data: account } = useAccountContext();
  const hasAccount = !!account;
  const { isWhiteLabel } = usePublicWhiteLabel();
  const { hideAirLogo } = useHideAirLogo();

  const onDownloadClick = useCallback(() => {
    if (boardId) {
      downloadPublicBoard({ boardId, trackLocation: 'board-stats-bar' });
    }
  }, [boardId, downloadPublicBoard]);

  return (
    <BoardBottombar>
      <div className="flex items-center gap-2">
        {!isWhiteLabel && !hideAirLogo && (
          <Link
            data-testid="PUBLIC_VIEWER_AIR_LOGO"
            href={Routes.marketing.root}
            rel="noopener noreferrer"
            target="_blank"
          >
            <VisuallyHidden>Visit Air's website</VisuallyHidden>
            <AirLogo className="h-3 ads:text-icon-emphasis text-gray-12" />
          </Link>
        )}
        {!!hasAccount && (
          <>
            {/* We `first:hidden` to hide the separator when the previous div doesn't exist */}
            <div className="h-4 w-px ads:bg-ads-gray-6 bg-gray-8 first:hidden" />
            <AccountInformation />
          </>
        )}
        {!!boardId && (
          <>
            {/* We `first:hidden` to hide the separator when the previous div doesn't exist */}
            <div className="h-4 w-px ads:bg-ads-gray-6 bg-gray-8 first:hidden" />
            <PublicBoardStats />
          </>
        )}
      </div>
      <div className="flex items-center gap-2">
        {!isSearchActive && !!boardId && isDownloadVisible && (
          <>
            <div className="h-4 w-px ads:bg-ads-gray-6 bg-gray-8 first:hidden" />
            <Tooltip label="Download board">
              <IconButton
                adsIcon
                appearance="ghost"
                color="gray"
                icon={DownloadIcon}
                label="Download board"
                onClick={onDownloadClick}
              />
            </Tooltip>
          </>
        )}
        {!hasAccount && !hideAirLogo && <PublicSignUpButton />}
      </div>
    </BoardBottombar>
  );
});

PublicBoardBottomBar.displayName = 'PublicBoardBottomBar';
